import { useMemo, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Badge, Button } from '@/components/atoms';
import { ApiError } from '@/services/api';
import { categoriesApi } from '@/services/articles';
import { sitesApi } from '@/services/config';
import {
  editoriasApi,
  type Editoria,
  type EditoriaPatch,
  type Estado,
  type FeedKind,
  type IngestResult,
  type Mode,
  type ProcMode,
  type RunResult,
} from '@/services/editorias';

import styles from './Editorias.module.css';

const FEED_LABEL: Record<FeedKind, string> = {
  google_news: 'Google News',
  rss: 'RSS',
  api: 'API',
  serp_trends: 'SERP · Trends',
  mos: 'MOS · NRCS',
};
const PROC_LABEL: Record<ProcMode, string> = {
  '1x1': '1×1 · reescreve 1 fonte',
  multi_fonte: 'Multi-fonte · agrupa + remonta 5W1H',
  enriquecer: 'Enriquecer · matéria pronta',
  dados: 'Dados → matéria',
};
const PROC_SHORT: Record<ProcMode, string> = {
  '1x1': '1×1', multi_fonte: 'multi-fonte', enriquecer: 'enriquecer', dados: 'dados',
};
const ESTADO_META: Record<Estado, { label: string; tone: 'ok' | 'warn' | 'info' }> = {
  no_ar: { label: 'no ar', tone: 'ok' },
  migrar: { label: 'migrar do N8N', tone: 'warn' },
  nova: { label: 'nova', tone: 'info' },
};
const MODES: Mode[] = ['off', 'semi', 'auto'];
// Modelos disponíveis hoje (só OpenAI ligado; a lista completa do BYOK vem no E4).
const LLM_MODELS = ['gpt-4o', 'gpt-4o-mini', 'gpt-4.1-mini', 'gpt-5.1'];

// Ao trocar a fonte, re-arma as variáveis para o formato daquela fonte.
const FEED_TEMPLATES: Record<FeedKind, { fonte: string; query: string; url: string; intervalo: string }> = {
  google_news: { fonte: "Google News (busca)", query: "", url: "", intervalo: "8h" },
  rss: { fonte: "Feed RSS do veículo", query: "", url: "https://", intervalo: "1h" },
  api: { fonte: "API (driver em breve)", query: "", url: "", intervalo: "15min" },
  serp_trends: { fonte: "SERP · Trending now (pt-BR)", query: "", url: "", intervalo: "1h" },
  mos: { fonte: "MOS · rundown do NRCS (push)", query: "", url: "", intervalo: "tempo real" },
};

// Monta a URL do Google News a partir do termo — o usuário nunca toca nos params.
function gnUrl(feed: { query?: string; url?: string }): string {
  const q = (feed.query ?? '').trim();
  if (q) return `https://news.google.com/rss/search?q=${encodeURIComponent(q)}&hl=pt-BR&gl=BR&ceid=BR:pt-419`;
  return feed.url || '(defina um termo de busca)';
}

export function Editorias() {
  const qc = useQueryClient();
  const [openId, setOpenId] = useState<string | null>('cultura');
  const [ingest, setIngest] = useState<IngestResult | null>(null);
  const [draft, setDraft] = useState<RunResult | null>(null);

  const ingestMut = useMutation({
    mutationFn: (slug: string) => editoriasApi.ingest(slug),
    onSuccess: (r) => setIngest(r),
  });
  const runMut = useMutation({
    mutationFn: (slug: string) => editoriasApi.run(slug),
    onSuccess: (r) => setDraft(r),
  });
  const [pubErr, setPubErr] = useState<string | null>(null);
  const publishMut = useMutation({
    mutationFn: (v: { slug: string; live: boolean }) =>
      v.live ? editoriasApi.publishLive(v.slug) : editoriasApi.publish(v.slug),
    onSuccess: (r) => {
      setDraft(r);
      setPubErr(null);
    },
    onError: (e: unknown) => {
      const detail =
        e instanceof ApiError && e.body && typeof e.body === 'object' && 'detail' in e.body
          ? String((e.body as { detail: unknown }).detail)
          : 'Falha ao salvar no WordPress.';
      setPubErr(detail);
    },
  });

  const { data: list = [], isLoading } = useQuery({
    queryKey: ['editorias'],
    queryFn: editoriasApi.list,
  });

  const mut = useMutation({
    mutationFn: (v: { slug: string; patch: EditoriaPatch }) =>
      editoriasApi.patch(v.slug, v.patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['editorias'] }),
  });
  const patch = (slug: string, p: EditoriaPatch) => mut.mutate({ slug, patch: p });

  const resumo = useMemo(() => {
    const c = (e: Estado) => list.filter((x) => x.estado === e).length;
    return { total: list.length, noAr: c('no_ar'), migrar: c('migrar'), nova: c('nova') };
  }, [list]);

  const aberta = list.find((x) => x.slug === openId) ?? null;

  // Edição do bloco Feed: rascunho local (snappy) + persiste no blur / na troca de fonte.
  const [feedDraft, setFeedDraft] = useState<{ slug: string; feed: Editoria['feed'] } | null>(null);
  const feedOf = (e: Editoria) =>
    feedDraft && feedDraft.slug === e.slug ? feedDraft.feed : e.feed;
  const editFeed = (p: Partial<Editoria['feed']>) => {
    if (aberta) setFeedDraft({ slug: aberta.slug, feed: { ...feedOf(aberta), ...p } });
  };
  const persistFeedNow = () => {
    if (aberta) patch(aberta.slug, { feed: feedOf(aberta) });
  };
  const changeFonte = (kind: FeedKind) => {
    if (!aberta) return;
    const tpl = FEED_TEMPLATES[kind];
    // Re-arma as variáveis para o formato da nova fonte.
    const next = {
      ...feedOf(aberta), kind,
      fonte: tpl.fonte, query: tpl.query, url: tpl.url, intervalo: tpl.intervalo,
    };
    setFeedDraft({ slug: aberta.slug, feed: next });
    patch(aberta.slug, { feed: next });
  };

  // Selects de config (E2b): sites cadastrados + categorias do 1º site.
  const sitesQuery = useQuery({ queryKey: ['wp-sites'], queryFn: () => sitesApi.list(true) });
  const firstSite = aberta?.pub.sites[0];
  const catsQuery = useQuery({
    queryKey: ['wp-cats', firstSite],
    queryFn: () => categoriesApi.list(firstSite),
    enabled: !!firstSite,
  });
  // Opções de site = cadastrados + todos os já referenciados por qualquer editoria
  // (ex.: ncnews, primário do .env). Acumula num ref que NUNCA encolhe — assim
  // desticar um site não faz a opção sumir (dá pra re-ticar sem recarregar).
  const knownSitesRef = useRef<Set<string>>(new Set());
  (sitesQuery.data ?? []).forEach((s) => knownSitesRef.current.add(s.slug));
  list.forEach((e) => e.pub.sites.forEach((s) => knownSitesRef.current.add(s)));
  const siteOptions = [...knownSitesRef.current].sort().map((slug) => {
    const reg = (sitesQuery.data ?? []).find((s) => s.slug === slug);
    return { slug, name: reg ? reg.name : slug };
  });

  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>Editorias</h1>
          <p className={styles.subtitle}>
            O motor automatizador. Cada editoria é um pipeline serial de 3 blocos —{' '}
            <strong>Feed</strong> → <strong>Processamento</strong> → <strong>Publicação</strong> —
            onde só os dados mudam. Linha nova, paralela ao N8N: nada em produção sai até o swap.
          </p>
        </div>
        <Button disabled title="Em breve — as editorias nascem do seed; criação manual virá depois">
          + Nova editoria
        </Button>
      </header>

      <div className={styles.resumo}>
        <span className={styles.chip}><b>{resumo.total}</b> editorias</span>
        <span className={`${styles.chip} ${styles.chipOk}`}><b>{resumo.noAr}</b> no ar</span>
        <span className={`${styles.chip} ${styles.chipWarn}`}><b>{resumo.migrar}</b> a migrar do N8N</span>
        <span className={`${styles.chip} ${styles.chipInfo}`}><b>{resumo.nova}</b> novas</span>
      </div>

      {isLoading && <p className={styles.proto}>A carregar editorias…</p>}

      <div className={styles.grid}>
        {list.map((e) => (
          <button
            key={e.slug}
            type="button"
            className={`${styles.card} ${openId === e.slug ? styles.cardOpen : ''}`}
            onClick={() => setOpenId(openId === e.slug ? null : e.slug)}
          >
            <div className={styles.cardHead}>
              <span className={styles.cardName}>{e.nome}</span>
              <Badge tone={ESTADO_META[e.estado].tone} size="xs">{ESTADO_META[e.estado].label}</Badge>
            </div>
            <div className={styles.pipe}>
              <span className={styles.pipeBlk}>{FEED_LABEL[e.feed.kind]}</span>
              <span className={styles.pipeArrow}>›</span>
              <span className={styles.pipeBlk}>{PROC_SHORT[e.proc.mode]}</span>
              <span className={styles.pipeArrow}>›</span>
              <span className={styles.pipeBlk}>{e.pub.status === 'publish' ? 'publica' : 'rascunho'}</span>
            </div>
            <div className={styles.cardFoot}>
              <span className={`${styles.modo} ${styles[`modo-${e.modo}`]}`}>{e.modo}</span>
              <span className={styles.cardMeta}>{e.pub.porHora}/h · {e.feed.intervalo}</span>
            </div>
          </button>
        ))}
      </div>

      {aberta && (
        <div className={styles.detalhe}>
          <div className={styles.detHead}>
            <div>
              <h2 className={styles.detTitle}>{aberta.nome}</h2>
              <span className={styles.detSub}>
                Pipeline · {FEED_LABEL[feedOf(aberta).kind]} → {PROC_LABEL[aberta.proc.mode]} → {aberta.pub.sites.join(', ')}
              </span>
            </div>
            <div className={styles.modeSel}>
              {MODES.map((m) => (
                <button
                  key={m}
                  type="button"
                  disabled={m === 'auto'}
                  title={m === 'auto' ? 'Execução automática de editorias chega em breve (sem agendador hoje)' : undefined}
                  className={`${styles.modeBtn} ${aberta.modo === m ? styles.modeBtnOn : ''}`}
                  onClick={() => patch(aberta.slug, { modo: m })}
                >
                  {m === 'auto' ? 'auto (em breve)' : m}
                </button>
              ))}
            </div>
          </div>

          {aberta.flags?.map((f) => (
            <p key={f} className={styles.flag}>{f}</p>
          ))}

          <div className={styles.blocos}>
            {/* BLOCO 1 — FEED */}
            <div className={styles.bloco}>
              <div className={styles.blocoHead}><span className={styles.blocoNum}>1</span> Feed</div>
              <Field label="Fonte">
                <select
                  className={styles.input}
                  value={feedOf(aberta).kind}
                  onChange={(ev) => changeFonte(ev.target.value as FeedKind)}
                >
                  {(Object.keys(FEED_LABEL) as FeedKind[]).map((k) => (
                    <option key={k} value={k}>{FEED_LABEL[k]}</option>
                  ))}
                </select>
              </Field>
              {feedOf(aberta).kind === 'google_news' && (
                <>
                  <Field label="Termo de busca">
                    <input
                      className={styles.input}
                      value={feedOf(aberta).query ?? ''}
                      placeholder="ex.: cultura, eleições, São Paulo"
                      onChange={(e) => editFeed({ query: e.target.value })}
                      onBlur={persistFeedNow}
                    />
                  </Field>
                  <Field label="URL gerada (travada)">
                    <div className={styles.ro} title={gnUrl(feedOf(aberta))}>{gnUrl(feedOf(aberta))}</div>
                  </Field>
                </>
              )}
              {feedOf(aberta).kind === 'rss' && (
                <Field label="URL do feed (RSS)">
                  <input
                    className={styles.input}
                    value={feedOf(aberta).url ?? ''}
                    placeholder="https://veiculo.com.br/feed"
                    onChange={(e) => editFeed({ url: e.target.value })}
                    onBlur={persistFeedNow}
                  />
                </Field>
              )}
              {feedOf(aberta).kind === 'api' && (
                <Field label="Fonte de dados">
                  <div className={styles.ro}>{feedOf(aberta).fonte}</div>
                </Field>
              )}
              {(feedOf(aberta).kind === 'serp_trends' || feedOf(aberta).kind === 'mos') && (
                <Field label="Origem">
                  <div className={styles.ro}>
                    {feedOf(aberta).kind === 'mos' ? 'push do NRCS — sem URL' : 'SERP Trends — automático'}
                  </div>
                </Field>
              )}
              <div className={styles.row}>
                <Field label="Itens/ciclo">
                  <input
                    type="number"
                    min={1}
                    max={50}
                    className={styles.input}
                    value={feedOf(aberta).itens}
                    onChange={(e) => editFeed({ itens: Number(e.target.value) || 1 })}
                    onBlur={persistFeedNow}
                  />
                </Field>
                <Field label="Intervalo">
                  <input
                    className={styles.input}
                    value={feedOf(aberta).intervalo}
                    onChange={(e) => editFeed({ intervalo: e.target.value })}
                    onBlur={persistFeedNow}
                  />
                </Field>
              </div>
            </div>

            <span className={styles.bigArrow}>›</span>

            {/* BLOCO 2 — PROCESSAMENTO */}
            <div className={styles.bloco}>
              <div className={styles.blocoHead}><span className={styles.blocoNum}>2</span> Processamento</div>
              <Field label="Modo">
                <select
                  className={styles.input}
                  value={aberta.proc.mode}
                  onChange={(ev) => patch(aberta.slug, { proc: { ...aberta.proc, mode: ev.target.value as ProcMode } })}
                >
                  {(Object.keys(PROC_LABEL) as ProcMode[]).map((k) => (
                    <option key={k} value={k}>{PROC_LABEL[k]}</option>
                  ))}
                </select>
              </Field>
              <Field label="Modelo LLM (BYOK)">
                <select
                  className={styles.input}
                  value={aberta.proc.modelo}
                  onChange={(e) => patch(aberta.slug, { proc: { ...aberta.proc, modelo: e.target.value } })}
                >
                  {[...new Set([aberta.proc.modelo, ...LLM_MODELS])].map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </Field>
              <Field label="Prompt"><div className={styles.ro}>prompt travado (edição na UI em breve)</div></Field>
              <label className={styles.check}>
                <input
                  type="checkbox"
                  checked={aberta.proc.dedup}
                  onChange={(ev) => patch(aberta.slug, { proc: { ...aberta.proc, dedup: ev.target.checked } })}
                />
                <span>Dedup de itens (fingerprint)</span>
              </label>
            </div>

            <span className={styles.bigArrow}>›</span>

            {/* BLOCO 3 — PUBLICAÇÃO */}
            <div className={styles.bloco}>
              <div className={styles.blocoHead}><span className={styles.blocoNum}>3</span> Publicação</div>
              <Field label="Sites WP">
                <div className={styles.sites}>
                  {siteOptions.map((s) => (
                    <label key={s.slug} className={styles.siteChk}>
                      <input
                        type="checkbox"
                        checked={aberta.pub.sites.includes(s.slug)}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...aberta.pub.sites, s.slug]
                            : aberta.pub.sites.filter((x) => x !== s.slug);
                          patch(aberta.slug, { pub: { ...aberta.pub, sites: next } });
                        }}
                      />
                      <span>{s.name}</span>
                    </label>
                  ))}
                  {siteOptions.length === 0 && (
                    <span className={styles.muted}>nenhum site cadastrado</span>
                  )}
                </div>
              </Field>
              <Field label="Categoria">
                {firstSite && catsQuery.data && catsQuery.data.length > 0 ? (
                  <select
                    className={styles.input}
                    value={aberta.pub.categoria_id ?? ''}
                    onChange={(e) => {
                      const id = Number(e.target.value) || undefined;
                      const cat = (catsQuery.data ?? []).find((c) => c.id === id);
                      patch(aberta.slug, {
                        pub: { ...aberta.pub, categoria_id: id, categoria: cat ? cat.name : aberta.pub.categoria },
                      });
                    }}
                  >
                    <option value="">{aberta.pub.categoria || '— escolher —'}</option>
                    {catsQuery.data.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                ) : (
                  <div className={styles.ro}>
                    {aberta.pub.categoria || '—'}
                    {firstSite && catsQuery.isLoading ? ' · a carregar…' : ''}
                    {firstSite && catsQuery.isError ? ' · categorias indisponíveis' : ''}
                  </div>
                )}
              </Field>
              <div className={styles.row}>
                <Field label="Status">
                  <select
                    className={styles.input}
                    value={aberta.pub.status}
                    onChange={(ev) => patch(aberta.slug, { pub: { ...aberta.pub, status: ev.target.value as 'publish' | 'draft' } })}
                  >
                    <option value="publish">Direto (publish)</option>
                    <option value="draft">Revisão (draft)</option>
                  </select>
                </Field>
                <Field label="Por hora"><div className={styles.ro}>{aberta.pub.porHora}</div></Field>
              </div>
              <Field label="Autor / AXIOMA"><div className={styles.ro}>{aberta.pub.autor} · story automática</div></Field>
            </div>
          </div>

          <div className={styles.detFoot}>
            <span className={styles.proto}>
              {mut.isPending ? 'A gravar…' : 'Conectado ao backend · alterações persistem (E1)'}
            </span>
            <div className={styles.footBtns}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => ingestMut.mutate(aberta.slug)}
                loading={ingestMut.isPending}
              >
                Rodar feed (prévia)
              </Button>
              <Button
                size="sm"
                onClick={() => runMut.mutate(aberta.slug)}
                loading={runMut.isPending}
              >
                Gerar rascunho (prévia)
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setPubErr(null);
                  publishMut.mutate({ slug: aberta.slug, live: false });
                }}
                loading={publishMut.isPending && publishMut.variables?.live === false}
              >
                Salvar rascunho no WP
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  if (!window.confirm('Publicar AO VIVO no WordPress? A matéria vai ao ar imediatamente.')) return;
                  setPubErr(null);
                  publishMut.mutate({ slug: aberta.slug, live: true });
                }}
                loading={publishMut.isPending && publishMut.variables?.live === true}
              >
                Publicar ao vivo
              </Button>
            </div>
          </div>

          {pubErr && <p className={styles.pubErr}>⚠ {pubErr}</p>}

          {ingest && ingest.slug === aberta.slug && (
            <div className={styles.ingest}>
              <div className={styles.ingestHead}>
                <strong>Feed → dedup</strong> · {ingest.total} itens ·{' '}
                <span className={styles.ingestNovos}>{ingest.novos} novos</span> ·{' '}
                <span className={styles.muted}>{ingest.duplicados} já vistos</span>
                {ingest.duplicados > 0 && ingest.novos === 0 && (
                  <span className={styles.ingestDedup}> dedup ✓</span>
                )}
                <button className={styles.runClose} onClick={() => setIngest(null)}>×</button>
              </div>
              <ul className={styles.ingestList}>
                {ingest.itens.map((it, i) => (
                  <li key={i} className={styles.ingestItem}>
                    <Badge tone={it.novo ? 'info' : 'neutral'} size="xs">{it.novo ? 'novo' : 'visto'}</Badge>
                    <span className={styles.ingestTitle}>{it.titulo}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {draft && draft.slug === aberta.slug && (
            <div className={styles.draft}>
              <div className={styles.ingestHead}>
                <strong>Rascunho gerado</strong> ·{' '}
                <span className={styles.muted}>{draft.mode} · {draft.modelo}</span>
                {typeof draft.custo_usd === 'number' && (
                  <span className={styles.muted}> · US$ {draft.custo_usd.toFixed(4)}</span>
                )}
                {draft.texto_fonte && (
                  <Badge tone={draft.texto_fonte === 'completo' ? 'ok' : 'neutral'} size="xs">
                    {draft.texto_fonte === 'completo' ? 'texto completo' : 'só resumo'}
                  </Badge>
                )}
                <Badge
                  tone={draft.wp_link ? (draft.status_destino === 'publish' ? 'ok' : 'info') : 'warn'}
                  size="xs"
                >
                  {draft.wp_link
                    ? draft.status_destino === 'publish'
                      ? 'PUBLICADO ao vivo'
                      : 'rascunho no WP'
                    : 'prévia · não publica'}
                </Badge>
                <button className={styles.runClose} onClick={() => setDraft(null)}>×</button>
              </div>
              {draft.gerado ? (
                <>
                  <h3 className={styles.draftTitle}>{draft.titulo}</h3>
                  <div
                    className={styles.draftBody}
                    dangerouslySetInnerHTML={{ __html: draft.html ?? '' }}
                  />
                  {draft.fontes && draft.fontes.length > 0 && (
                    <p className={styles.draftFontes}>
                      Fonte: {draft.fontes.map((f) => f.titulo).join(' · ')}
                    </p>
                  )}
                  {draft.wp_link && (
                    <p className={styles.draftFontes}>
                      ✓ {draft.status_destino === 'publish' ? 'Publicado ao vivo' : 'Rascunho salvo (draft)'} ·{' '}
                      <a href={draft.wp_link} target="_blank" rel="noreferrer" className={styles.wpLink}>
                        ver no WordPress →
                      </a>
                    </p>
                  )}
                </>
              ) : (
                <p className={styles.muted}>{draft.motivo ?? 'Nada gerado.'}</p>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      {children}
    </label>
  );
}

export type { Editoria };
